import React from 'react';
import { Helmet } from "react-helmet";

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Tanarela - Premium Landscape Design Materials Australia</title>
        <meta name="description" content="Tanarela - Premium landscape design materials and natural stone products for creating stunning outdoor spaces in Australia" />
      </Helmet>

      <div className="policy-container">
      <div className="policy-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to Tanarela ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
            tanarela.com and purchase our premium landscape design materials and natural stone products.
          </p>
          <p>
            Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information</h3>
          <p>We collect personal information that you voluntarily provide to us when you:</p>
          <ul>
            <li>Register on the website</li>
            <li>Place an order for our landscape design materials or natural stone products</li>
            <li>Subscribe to our newsletter</li>
            <li>Contact us with inquiries</li>
            <li>Participate in surveys or promotions</li>
          </ul>
          
          <p>The personal information we collect may include:</p>
          <ul>
            <li>Name and contact information (email address, phone number, postal address)</li>
            <li>Billing and shipping addresses</li>
            <li>Payment information (processed securely through our payment providers)</li>
            <li>Account credentials (username and password)</li>
            <li>Purchase history and preferences</li>
            <li>Communication preferences</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <p>When you visit our website, we automatically collect certain information about your device, including:</p>
          <ul>
            <li>IP address and browser type</li>
            <li>Operating system and device information</li>
            <li>Referring URLs and pages viewed</li>
            <li>Date and time of visits</li>
            <li>Clickstream data and browsing behavior</li>
          </ul>

          <h3>2.3 Cookies and Tracking Technologies</h3>
          <p>
            We use cookies, web beacons, and similar tracking technologies to collect information about your browsing activities. 
            This helps us improve your experience, analyze site usage, and deliver personalized content and advertisements.
          </p>
          <p>You can control cookie preferences through your browser settings, though disabling cookies may limit certain website functionality.</p>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect for various purposes, including:</p>
          <ul>
            <li>Processing and fulfilling your orders for landscape design materials and natural stone products</li>
            <li>Managing your account and providing customer support</li>
            <li>Sending order confirmations, shipping updates, and transactional communications</li>
            <li>Responding to your inquiries and requests</li>
            <li>Improving our website, products, and services</li>
            <li>Personalizing your shopping experience</li>
            <li>Sending marketing communications and promotional offers (with your consent)</li>
            <li>Conducting analytics and research to understand customer preferences</li>
            <li>Detecting and preventing fraud and security threats</li>
            <li>Complying with legal obligations and enforcing our terms and conditions</li>
          </ul>
        </section>

        <section>
          <h2>4. How We Share Your Information</h2>
          <p>We may share your information with third parties in the following circumstances:</p>
          
          <h3>4.1 Service Providers</h3>
          <p>
            We engage trusted third-party service providers to perform functions on our behalf, such as payment processing, 
            order fulfillment, shipping and delivery, email services, marketing, analytics, and customer support. 
            These providers have access to your personal information only to perform specific tasks and are obligated to 
            protect your information.
          </p>

          <h3>4.2 Business Transfers</h3>
          <p>
            In the event of a merger, acquisition, reorganization, sale of assets, or bankruptcy, your information may be 
            transferred to the successor entity.
          </p>

          <h3>4.3 Legal Requirements</h3>
          <p>We may disclose your information when required by law, such as:</p>
          <ul>
            <li>To comply with legal obligations, court orders, or government requests</li>
            <li>To enforce our terms and conditions and other agreements</li>
            <li>To protect our rights, property, and safety, or that of our customers and the public</li>
            <li>To detect, prevent, or address fraud, security, or technical issues</li>
          </ul>

          <h3>4.4 With Your Consent</h3>
          <p>We may share your information with third parties when you have given us explicit consent to do so.</p>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your personal information 
            against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul>
            <li>Encryption of sensitive data during transmission (SSL/TLS)</li>
            <li>Secure storage systems with access controls</li>
            <li>Regular security assessments and updates</li>
            <li>Employee training on data protection practices</li>
            <li>Secure payment processing through PCI-DSS compliant providers</li>
          </ul>
          <p>
            However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to 
            protect your personal information, we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, 
            unless a longer retention period is required or permitted by law. Retention periods vary depending on the type of 
            information and the purpose for which it was collected:
          </p>
          <ul>
            <li>Account information: Retained while your account is active and for a reasonable period afterward</li>
            <li>Transaction records: Retained for tax, accounting, and legal purposes (typically 7 years)</li>
            <li>Marketing communications: Retained until you unsubscribe or request deletion</li>
            <li>Analytics data: Aggregated and anonymized data may be retained indefinitely</li>
          </ul>
        </section>

        <section>
          <h2>7. Your Privacy Rights</h2>
          <p>Depending on your location, you may have certain rights regarding your personal information:</p>
          
          <h3>7.1 Access and Portability</h3>
          <p>You have the right to request access to the personal information we hold about you and receive a copy in a structured, commonly used format.</p>

          <h3>7.2 Correction</h3>
          <p>You can request that we correct any inaccurate or incomplete personal information.</p>

          <h3>7.3 Deletion</h3>
          <p>You can request that we delete your personal information, subject to certain legal exceptions.</p>

          <h3>7.4 Restriction and Objection</h3>
          <p>You can request that we restrict the processing of your personal information or object to certain processing activities.</p>

          <h3>7.5 Withdraw Consent</h3>
          <p>Where we rely on your consent to process your information, you can withdraw that consent at any time.</p>

          <h3>7.6 Opt-Out of Marketing</h3>
          <p>You can unsubscribe from our marketing communications at any time by clicking the unsubscribe link in our emails or contacting us directly.</p>

          <p>
            To exercise any of these rights, please contact us using the information provided in the "Contact Us" section below. 
            We will respond to your request within a reasonable timeframe and in accordance with applicable law.
          </p>
        </section>

        <section>
          <h2>8. Australian Privacy Principles</h2>
          <p>
            As an Australian business, Tanarela complies with the Australian Privacy Principles (APPs) under the Privacy Act 1988. 
            We are committed to handling your personal information in accordance with these principles, including:
          </p>
          <ul>
            <li>Collecting information fairly and lawfully</li>
            <li>Using and disclosing information only for the purposes for which it was collected</li>
            <li>Maintaining the quality and security of personal information</li>
            <li>Providing access to and correction of personal information</li>
            <li>Being transparent about how we handle personal information</li>
          </ul>
          <p>
            If you have concerns about how we handle your personal information, you have the right to lodge a complaint with 
            the Office of the Australian Information Commissioner (OAIC).
          </p>
        </section>

        <section>
          <h2>9. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than Australia, including countries that may 
            not have the same data protection laws. When we transfer your information internationally, we take appropriate 
            safeguards to ensure your information remains protected, such as using standard contractual clauses or ensuring 
            the recipient country has adequate data protection laws.
          </p>
        </section>

        <section>
          <h2>10. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites, including social media platforms, payment processors, and 
            partner sites. We are not responsible for the privacy practices of these third parties. We encourage you to review 
            their privacy policies before providing any personal information.
          </p>
        </section>

        <section>
          <h2>11. Children's Privacy</h2>
          <p>
            Our website and services are not directed to children under the age of 18. We do not knowingly collect personal 
            information from children. If you believe we have inadvertently collected information from a child, please contact 
            us immediately, and we will take steps to delete such information.
          </p>
        </section>

        <section>
          <h2>12. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, 
            or other factors. We will notify you of any material changes by posting the updated policy on our website with a new 
            "Last Updated" date. Your continued use of our website after such changes constitutes your acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>13. Contact Us</h2>
          <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:</p>
          <div className="contact-info">
            <p><strong>Tanarela</strong></p>
            <p>Email: <a href="mailto:tanarela@hotmail.com">tanarela@hotmail.com</a></p>
            <p>Website: https://tanarela.com</p>
            <p>Address: Australia</p>
          </div>
          <p>
            We are committed to addressing your concerns and will respond to your inquiry within a reasonable timeframe.
          </p>
        </section>

        <section>
          <h2>14. Consent</h2>
          <p>
            By using our website and services, you consent to the collection, use, and disclosure of your personal information 
            as described in this Privacy Policy. If you do not agree with this policy, please do not use our website or provide 
            us with your personal information.
          </p>
        </section>
      </div>
    </div>
  
    </>);
};

export default PrivacyPolicy;